import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Shield, UserX, Clock, History, Activity, Pill, FileText,
  Heart, AlertTriangle, UserCheck, X, ChevronRight, CheckCircle,
  Lock, Unlock, Eye, EyeOff, LogOut, ChevronDown, ChevronUp,
  RotateCcw, AlertCircle, Trash2
} from 'lucide-react';

const DataConsent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [consents, setConsents] = useState([]);
  const [careTeam, setCareTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDoctor, setExpandedDoctor] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Consent form state
  const [consentForm, setConsentForm] = useState({
    symptoms: true,
    medications: true,
    vitals: true,
    conditions: true,
    allergies: true,
    labResults: true,
    socialHistory: false,
    mentalHealth: false,
    financialInfo: false,
    historyMonths: 12,
    allowRealTime: true,
    allowExport: false,
    allowSharing: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [consentsRes, profileRes] = await Promise.all([
        api.get('/consent/my-consents').catch(() => ({ data: { data: [] } })),
        api.get('/patient-profile/me').catch(() => null)
      ]);
      
      setConsents(consentsRes.data?.data || []);
      
      // Normalize care team: flatten providerId into top-level fields
      const rawTeam = profileRes?.data?.data?.careTeam || [];
      const primary = profileRes?.data?.data?.primaryCareProvider;
      const normalized = [];
      // Add primary care provider if present and not already in careTeam
      if (primary && primary._id) {
        normalized.push({
          _id: primary._id,
          name: primary.name,
          email: primary.email,
          specialization: primary.specialization,
          role: 'primary'
        });
      }
      for (const m of rawTeam) {
        const doc = m.providerId || m;
        const docId = doc._id || doc;
        // Skip if already added as primary
        if (normalized.some(n => n._id === docId.toString?.() || n._id === docId)) continue;
        normalized.push({
          _id: typeof docId === 'object' ? docId.toString?.() || docId : docId,
          name: doc.name || 'Doctor',
          email: doc.email || '',
          specialization: doc.specialization || m.role || 'General Practice',
          role: m.role || 'specialist'
        });
      }
      setCareTeam(normalized);
    } catch (error) {
      console.error('Failed to load consent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!selectedDoctor) return;
    
    setSaving(true);
    try {
      await api.post('/consent/grant', {
        doctorId: selectedDoctor._id,
        scope: {
          symptoms: consentForm.symptoms,
          medications: consentForm.medications,
          vitals: consentForm.vitals,
          conditions: consentForm.conditions,
          allergies: consentForm.allergies,
          labResults: consentForm.labResults,
          socialHistory: consentForm.socialHistory,
          mentalHealth: consentForm.mentalHealth,
          financialInfo: consentForm.financialInfo,
          historicalDataMonths: consentForm.historyMonths,
          realTimeUpdates: consentForm.allowRealTime,
          allowExport: consentForm.allowExport,
          allowSharing: consentForm.allowSharing
        },
        purpose: 'Primary care coordination'
      });
      toast.success('Data access granted to ' + selectedDoctor.name);
      setShowGrantModal(false);
      setSelectedDoctor(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to grant access');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (consentId) => {
    if (!confirm('Are you sure you want to revoke this doctor\'s access to your data?')) return;
    
    try {
      await api.post(`/consent/revoke/${consentId}`);
      toast.success('Data access revoked');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke access');
    }
  };

  const handleLeaveDoctor = async (doctorId, doctorName) => {
    if (!confirm(`Are you sure you want to leave ${doctorName}'s care team? This will automatically revoke all data access.`)) return;
    
    try {
      await api.post(`/patient-profile/leave-doctor/${doctorId}`);
      toast.success(`Left ${doctorName}'s care team. All data access has been revoked.`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to leave care team');
    }
  };

  const loadAuditLogs = async (consentId) => {
    try {
      const res = await api.get(`/consent/audit-log/${consentId}`);
      setAuditLogs(res.data?.data?.logs || []);
      setShowAuditModal(true);
    } catch (err) {
      toast.error('Failed to load audit logs');
    }
  };

  const getPermissionIcon = (granted) => granted ? 
    <CheckCircle className="w-4 h-4 text-emerald-500" /> : 
    <X className="w-4 h-4 text-slate-300" />;

  const getStatusBadge = (status) => {
    const configs = {
      active: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Active Access' },
      revoked: { bg: 'bg-red-50', text: 'text-red-600', label: 'Revoked' },
      suspended: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Suspended' },
      expired: { bg: 'bg-slate-50', text: 'text-slate-500', label: 'Expired' }
    };
    const config = configs[status] || configs.active;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const doctorsWithAccess = careTeam.filter(doc => 
    consents.some(c => c.doctorId?._id === doc._id && c.status === 'active')
  );
  
  const doctorsWithoutAccess = careTeam.filter(doc => 
    !consents.some(c => c.doctorId?._id === doc._id && c.status === 'active')
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Shield className="w-12 h-12 text-slate-200" />
          <p className="text-slate-500 text-sm">Loading data privacy settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="font-headline text-3xl font-bold text-slate-900 tracking-tight">
              Data Privacy & Consent
            </h1>
          </div>
          <p className="text-slate-500">
            Control which doctors can access your health information and what they can see.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-outline-variant/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{doctorsWithAccess.length}</p>
                <p className="text-sm text-slate-500">Doctors with access</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-outline-variant/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{doctorsWithoutAccess.length}</p>
                <p className="text-sm text-slate-500">Restricted doctors</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-outline-variant/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <History className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{consents.length}</p>
                <p className="text-sm text-slate-500">Total consents</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Consents */}
        <div className="mb-8">
          <h2 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <Unlock className="w-5 h-5 text-emerald-500" />
            Active Data Access
          </h2>
          
          {doctorsWithAccess.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-outline-variant/10">
              <Lock className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No doctors currently have access to your data.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {doctorsWithAccess.map(doctor => {
                const consent = consents.find(c => c.doctorId?._id === doctor._id && c.status === 'active');
                const isExpanded = expandedDoctor === doctor._id;
                
                return (
                  <div key={doctor._id} className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <span className="text-lg font-bold text-primary">
                              {doctor.name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900">Dr. {doctor.name}</h3>
                            <p className="text-sm text-slate-500">{doctor.specialization || 'General Practitioner'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getStatusBadge(consent?.status)}
                              <span className="text-xs text-slate-500">
                                Since {new Date(consent?.grantedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => loadAuditLogs(consent._id)}
                            className="px-3 py-2 text-slate-500 hover:bg-slate-50 rounded-lg text-sm font-medium transition-all"
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setExpandedDoctor(isExpanded ? null : doctor._id)}
                            className="px-3 py-2 text-slate-500 hover:bg-slate-50 rounded-lg text-sm font-medium transition-all"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.symptoms)}
                              <span className="text-sm text-slate-600">Symptoms</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.medications)}
                              <span className="text-sm text-slate-600">Medications</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.vitals)}
                              <span className="text-sm text-slate-600">Vitals</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.conditions)}
                              <span className="text-sm text-slate-600">Conditions</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.labResults)}
                              <span className="text-sm text-slate-600">Lab Results</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.allergies)}
                              <span className="text-sm text-slate-600">Allergies</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.socialHistory)}
                              <span className="text-sm text-slate-600">Social History</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.mentalHealth)}
                              <span className="text-sm text-slate-600">Mental Health</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(consent?.scope?.financialInfo)}
                              <span className="text-sm text-slate-600">Financial Info</span>
                            </div>
                          </div>
                          
                          <div className="bg-slate-50 rounded-xl p-4 mb-4">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-600">History Access:</span>
                              <span className="font-bold text-slate-900">{consent?.scope?.historicalDataMonths || 12} months</span>
                            </div>
                            <div className="flex items-center justify-between text-sm mt-2">
                              <span className="text-slate-600">Real-time Updates:</span>
                              <span className="font-bold text-slate-900">{consent?.scope?.realTimeUpdates ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm mt-2">
                              <span className="text-slate-600">Export/Sharing:</span>
                              <span className="font-bold text-slate-900">{consent?.scope?.allowExport ? 'Allowed' : 'Denied'}</span>
                            </div>
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => handleRevoke(consent._id)}
                              className="flex-1 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-bold hover:bg-red-100 transition-all flex items-center justify-center gap-2"
                            >
                              <Lock className="w-4 h-4" />
                              Revoke Access
                            </button>
                            <button
                              onClick={() => handleLeaveDoctor(doctor._id, doctor.name)}
                              className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                            >
                              <LogOut className="w-4 h-4" />
                              Leave Care Team
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Doctors Without Access */}
        {doctorsWithoutAccess.length > 0 && (
          <div className="mb-8">
            <h2 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-slate-500" />
              Care Team (No Data Access)
            </h2>
            <div className="space-y-4">
              {doctorsWithoutAccess.map(doctor => (
                <div key={doctor._id} className="bg-white rounded-2xl p-5 border border-outline-variant/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <span className="text-lg font-bold text-slate-500">
                          {doctor.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">Dr. {doctor.name}</h3>
                        <p className="text-sm text-slate-500">{doctor.specialization || 'General Practitioner'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedDoctor(doctor); setShowGrantModal(true); }}
                      className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all"
                    >
                      Grant Access
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Privacy Tips */}
        <div className="bg-blue-50 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Privacy Tips</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• You control exactly what each doctor can see</li>
                <li>• Leaving a care team automatically revokes all access</li>
                <li>• All changes are logged for your records</li>
                <li>• You can modify permissions at any time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Grant Access Modal */}
      {showGrantModal && selectedDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg text-slate-900">
                  Grant Access to Dr. {selectedDoctor.name}
                </h3>
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Permissions */}
              <div>
                <h4 className="font-bold text-sm text-slate-900 mb-3">What can they access?</h4>
                <div className="space-y-2">
                  {[
                    { key: 'symptoms', label: 'Symptoms & Health Issues' },
                    { key: 'medications', label: 'Medications' },
                    { key: 'vitals', label: 'Vital Signs' },
                    { key: 'conditions', label: 'Medical Conditions' },
                    { key: 'allergies', label: 'Allergies' },
                    { key: 'labResults', label: 'Lab Results' },
                    { key: 'socialHistory', label: 'Social History' },
                    { key: 'mentalHealth', label: 'Mental Health' },
                    { key: 'financialInfo', label: 'Financial Information' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100">
                      <input
                        type="checkbox"
                        checked={consentForm[key]}
                        onChange={(e) => setConsentForm({ ...consentForm, [key]: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* History Range */}
              <div>
                <h4 className="font-bold text-sm text-slate-900 mb-3">History Access</h4>
                <label className="block text-sm text-slate-600 mb-2">
                  How many months of history can they see?
                </label>
                <input
                  type="range"
                  min="0"
                  max="120"
                  value={consentForm.historyMonths}
                  onChange={(e) => setConsentForm({ ...consentForm, historyMonths: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>None</span>
                  <span className="font-bold text-primary">{consentForm.historyMonths} months</span>
                  <span>10 years</span>
                </div>
              </div>

              {/* Additional Options */}
              <div>
                <h4 className="font-bold text-sm text-slate-900 mb-3">Additional Options</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100">
                    <input
                      type="checkbox"
                      checked={consentForm.allowRealTime}
                      onChange={(e) => setConsentForm({ ...consentForm, allowRealTime: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="text-sm text-slate-700 block">Real-time updates</span>
                      <span className="text-xs text-slate-500">Doctor sees new data immediately</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100">
                    <input
                      type="checkbox"
                      checked={consentForm.allowExport}
                      onChange={(e) => setConsentForm({ ...consentForm, allowExport: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="text-sm text-slate-700 block">Allow export</span>
                      <span className="text-xs text-slate-500">Doctor can export your data</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowGrantModal(false)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleGrant}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? 'Granting...' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg text-slate-900">Access History</h3>
                <button
                  onClick={() => setShowAuditModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {auditLogs.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No audit logs found</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <History className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">{log.action}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                        {log.details && (
                          <p className="text-xs text-slate-600 mt-1">{JSON.stringify(log.details)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataConsent;
